import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export interface Vendor {
  id: string;
  name: string;
}

interface VendorContextType {
  vendors: Vendor[];
  selectedVendor: Vendor | null;
  selectedVendorId: string;
  setSelectedVendorId: (id: string) => void;
  loadVendors: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

const VendorContext = createContext<VendorContextType | undefined>(undefined);

export function VendorProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [selectedVendorId, setSelectedVendorIdState] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get the full vendor object for the selected ID
  const selectedVendor = vendors.find(v => v.id === selectedVendorId) || null;

  // Load saved vendor from localStorage on mount
  useEffect(() => {
    if (user) {
      const savedVendor = localStorage.getItem(`selectedVendor_${user.id}`);
      if (savedVendor) {
        setSelectedVendorIdState(savedVendor);
      }
    }
  }, [user]);

  // Save vendor to localStorage when changed
  const setSelectedVendorId = (id: string) => {
    setSelectedVendorIdState(id);
    if (user) {
      if (id) {
        localStorage.setItem(`selectedVendor_${user.id}`, id);
      } else {
        localStorage.removeItem(`selectedVendor_${user.id}`);
      }
    }
  };

  // Load vendors from API
  const loadVendors = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/simplerfid/vendors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const vendorsList = response.data.vendors || [];
      setVendors(vendorsList);
      
      // If we have a saved vendor ID but it's not in the list, clear it
      if (selectedVendorId && !vendorsList.find((v: Vendor) => v.id === selectedVendorId)) {
        setSelectedVendorId('');
      }
      
      // If no vendor selected and we have vendors, don't auto-select
      // Let user choose explicitly
    } catch (err: any) {
      console.error('Error loading vendors:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        setError('Authentication failed. Please log in again.');
      } else {
        setError(err.response?.data?.error || 'Failed to load vendors');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Load vendors when user logs in
  useEffect(() => {
    if (user) {
      loadVendors();
    } else {
      setVendors([]);
      setSelectedVendorIdState('');
    }
  }, [user]);

  return (
    <VendorContext.Provider value={{
      vendors,
      selectedVendor,
      selectedVendorId,
      setSelectedVendorId,
      loadVendors,
      isLoading,
      error
    }}>
      {children}
    </VendorContext.Provider>
  );
}

export function useVendor() {
  const context = useContext(VendorContext);
  if (context === undefined) {
    throw new Error('useVendor must be used within a VendorProvider');
  }
  return context;
}
